import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, User, Star, Clock, GraduationCap, CheckCircle, 
  MessageSquare, Calendar, Award, BookOpen, Settings, CreditCard,
  AlertCircle, Loader2, Plus, X, Eye, EyeOff, FileText, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import FeedbackButton from '@/components/FeedbackButton';
import QuestionBankForm from '@/components/QuestionBankForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TutorDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState({});
  const [newSubject, setNewSubject] = useState('');
  const [newQualification, setNewQualification] = useState('');
  const [myQuestions, setMyQuestions] = useState([]);
  const [myUploads, setMyUploads] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);

  const [formData, setFormData] = useState({
    bio: '',
    experience_years: 0,
    subjects: [],
    qualifications: [],
    hourly_rate: 40,
    availability: '',
    teaching_style: '',
    profile_image: ''
  });

  useEffect(() => {
    loadProfile();
    loadInquiries();
    loadSubscriptionPlans();
    loadMyQuestions();
    loadMyUploads();
  }, []);

  const loadMyQuestions = async () => {
    setQuestionsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await axios.get(`${API}/questions?createdBy=tutor`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to only show this tutor's questions
      const filtered = response.data.questions?.filter(q => q.creatorId === user.id) || [];
      setMyQuestions(filtered);
    } catch (error) {
      console.error('Error loading questions:', error);
      setMyQuestions([]);
    }
    setQuestionsLoading(false);
  };

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tutor/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data.profile);
      setFormData({
        bio: response.data.profile.bio || '',
        experience_years: response.data.profile.experience_years || 0,
        subjects: response.data.profile.subjects || [],
        qualifications: response.data.profile.qualifications || [],
        hourly_rate: response.data.profile.hourly_rate || 40,
        availability: response.data.profile.availability || '',
        teaching_style: response.data.profile.teaching_style || '',
        profile_image: response.data.profile.profile_image || ''
      });
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadInquiries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tutor/inquiries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInquiries(response.data.inquiries || []);
    } catch (error) {
      console.error('Error loading inquiries:', error);
    }
  };

  const loadSubscriptionPlans = async () => {
    try {
      const response = await axios.get(`${API}/tutor/subscription-plans`);
      setSubscriptionPlans(response.data.plans || {});
    } catch (error) {
      console.error('Error loading plans:', error);
    }
  };

  const loadMyUploads = async () => {
    try {
      const response = await axios.get(`${API}/tutor/my-uploads`, { headers });
      setMyUploads(response.data.uploads || []);
    } catch (error) {
      console.error('Error loading uploads:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addSubject = () => {
    if (newSubject && !formData.subjects.includes(newSubject)) {
      setFormData(prev => ({
        ...prev,
        subjects: [...prev.subjects, newSubject]
      }));
      setNewSubject('');
    }
  };

  const removeSubject = (subject) => {
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects.filter(s => s !== subject)
    }));
  };

  const addQualification = () => {
    if (newQualification && !formData.qualifications.includes(newQualification)) {
      setFormData(prev => ({
        ...prev,
        qualifications: [...prev.qualifications, newQualification]
      }));
      setNewQualification('');
    }
  };

  const removeQualification = (qual) => {
    setFormData(prev => ({
      ...prev,
      qualifications: prev.qualifications.filter(q => q !== qual)
    }));
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/tutor/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile saved and submitted for approval!');
      loadProfile();
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSubscribe = async (planId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/tutor/subscribe`, 
        { planId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      } else {
        toast.success('Subscription activated!');
        loadProfile();
      }
    } catch (error) {
      toast.error('Failed to subscribe');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-500">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500">Rejected</Badge>;
      default:
        return <Badge className="bg-yellow-500">Pending Review</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-sm shadow-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/categories')} className="text-white hover:bg-white/20">
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <User className="h-6 w-6 text-white" />
              <h1 className="text-2xl font-bold text-white">Tutor Dashboard</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <FeedbackButton userType="tutor" variant="default" />
            {getStatusBadge(profile?.approval_status)}
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-4 md:p-8">
        {/* Status Alert */}
        {profile?.approval_status === 'pending' && (
          <Card className="mb-6 border-yellow-400 bg-yellow-50">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="font-semibold text-yellow-800">Profile Pending Approval</p>
                <p className="text-sm text-yellow-700">Your profile is being reviewed by our team. Once approved, you'll appear in the tutor marketplace.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {profile?.approval_status === 'rejected' && (
          <Card className="mb-6 border-red-400 bg-red-50">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <div>
                <p className="font-semibold text-red-800">Profile Rejected</p>
                <p className="text-sm text-red-700">Reason: {profile?.rejection_reason || 'Please update your profile and resubmit.'}</p>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="profile">My Profile</TabsTrigger>
            <TabsTrigger value="questions" className="flex items-center gap-1">
              <FileText className="h-4 w-4" />
              Questions
            </TabsTrigger>
            <TabsTrigger value="subscription">Subscription</TabsTrigger>
            <TabsTrigger value="inquiries">Inquiries ({inquiries.length})</TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" /> Edit Your Profile
                </CardTitle>
                <CardDescription>
                  Complete your profile to appear in the tutor marketplace. Changes require re-approval.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Bio */}
                <div className="space-y-2">
                  <Label>Bio / About Me</Label>
                  <Textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    placeholder="Tell students and parents about yourself, your teaching style, and experience..."
                    rows={4}
                  />
                </div>

                {/* Experience & Rate */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Years of Experience</Label>
                    <Input
                      type="number"
                      name="experience_years"
                      value={formData.experience_years}
                      onChange={handleInputChange}
                      min={0}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Hourly Rate (£)</Label>
                    <Input
                      type="number"
                      name="hourly_rate"
                      value={formData.hourly_rate}
                      onChange={handleInputChange}
                      min={0}
                    />
                  </div>
                </div>

                {/* Subjects */}
                <div className="space-y-2">
                  <Label>Subjects You Teach</Label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={newSubject}
                      onChange={(e) => setNewSubject(e.target.value)}
                      placeholder="Add a subject..."
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSubject())}
                    />
                    <Button type="button" onClick={addSubject} size="icon">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.subjects.map((subject, idx) => (
                      <Badge key={idx} variant="secondary" className="flex items-center gap-1 px-3 py-1">
                        {subject}
                        <X className="h-3 w-3 cursor-pointer" onClick={() => removeSubject(subject)} />
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Qualifications */}
                <div className="space-y-2">
                  <Label>Qualifications & Certifications</Label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={newQualification}
                      onChange={(e) => setNewQualification(e.target.value)}
                      placeholder="Add a qualification..."
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addQualification())}
                    />
                    <Button type="button" onClick={addQualification} size="icon">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.qualifications.map((qual, idx) => (
                      <Badge key={idx} variant="outline" className="flex items-center gap-1 px-3 py-1">
                        <GraduationCap className="h-3 w-3" />
                        {qual}
                        <X className="h-3 w-3 cursor-pointer" onClick={() => removeQualification(qual)} />
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Availability & Teaching Style */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Availability</Label>
                    <Input
                      name="availability"
                      value={formData.availability}
                      onChange={handleInputChange}
                      placeholder="e.g., Weekdays 4-8pm, Weekends"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Teaching Style</Label>
                    <Input
                      name="teaching_style"
                      value={formData.teaching_style}
                      onChange={handleInputChange}
                      placeholder="e.g., Patient, Interactive, Results-focused"
                    />
                  </div>
                </div>

                {/* Save Button */}
                <Button 
                  onClick={handleSaveProfile} 
                  disabled={saving}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Profile & Submit for Approval'
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Question Bank Tab */}
          <TabsContent value="questions">
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100 text-sm">Total Questions</p>
                        <p className="text-2xl font-bold">{myQuestions.length}</p>
                      </div>
                      <FileText className="h-8 w-8 text-purple-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-amber-100 text-sm">Pending Approval</p>
                        <p className="text-2xl font-bold">{myQuestions.filter(q => q.status === 'pending').length}</p>
                      </div>
                      <Clock className="h-8 w-8 text-amber-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100 text-sm">Approved</p>
                        <p className="text-2xl font-bold">{myQuestions.filter(q => q.status === 'approved').length}</p>
                      </div>
                      <CheckCircle className="h-8 w-8 text-green-200" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Question Form */}
              <QuestionBankForm 
                userType="tutor"
                isVerifiedTutor={profile?.isVerifiedTutor || false}
                onQuestionAdded={() => { loadMyQuestions(); loadMyUploads(); }}
              />

              {/* My Uploaded Papers */}
              <Card className="shadow-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="h-5 w-5" /> My Uploaded Papers
                  </CardTitle>
                  <CardDescription>
                    Question papers you've uploaded for review
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {myUploads.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Upload className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>No papers uploaded yet</p>
                      <p className="text-sm">Use the "Upload" tab above to upload question papers!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {myUploads.map((upload, idx) => (
                        <div key={upload.id || idx} className="p-4 border rounded-lg hover:bg-gray-50">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-gray-800">{upload.name}</p>
                              <p className="text-sm text-gray-600 mt-1">{upload.description || 'No description'}</p>
                              <div className="flex flex-wrap gap-2 mt-2">
                                <Badge variant="outline">{upload.stage}</Badge>
                                <Badge variant="outline">{upload.subject}</Badge>
                                <Badge variant="secondary">{upload.fileName}</Badge>
                                <Badge className={
                                  upload.status === 'approved' ? 'bg-green-100 text-green-700' : 
                                  upload.status === 'pending' ? 'bg-amber-100 text-amber-700' : 
                                  'bg-red-100 text-red-700'
                                }>
                                  {upload.status}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* My Questions List */}
              <Card className="shadow-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5" /> My Questions
                  </CardTitle>
                  <CardDescription>
                    Questions you've created for the platform
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {questionsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                    </div>
                  ) : myQuestions.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>No questions created yet</p>
                      <p className="text-sm">Use the form above to add your first question!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {myQuestions.slice(0, 10).map((question, idx) => (
                        <div key={question.id || idx} className="p-4 border rounded-lg hover:bg-gray-50">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-gray-800 line-clamp-2">{question.question}</p>
                              <div className="flex flex-wrap gap-2 mt-2">
                                <Badge variant="outline">{question.stage}</Badge>
                                <Badge variant="outline">{question.subject}</Badge>
                                <Badge variant="outline">{question.paperType}</Badge>
                                <Badge className={question.status === 'approved' ? 'bg-green-100 text-green-700' : question.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                                  {question.status}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {myQuestions.length > 10 && (
                        <p className="text-center text-gray-500 text-sm">
                          Showing 10 of {myQuestions.length} questions
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="subscription">
            <Card className="shadow-xl mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" /> Current Subscription
                </CardTitle>
              </CardHeader>
              <CardContent>
                {profile?.subscription?.status === 'active' ? (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="font-semibold text-green-800">Active Subscription</span>
                    </div>
                    <p className="text-sm text-green-700">
                      Plan: {subscriptionPlans[profile.subscription.planId]?.name || profile.subscription.planId}
                    </p>
                    <p className="text-sm text-green-700">
                      Expires: {new Date(profile.subscription.endDate).toLocaleDateString()}
                    </p>
                  </div>
                ) : (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-5 w-5 text-yellow-600" />
                      <span className="font-semibold text-yellow-800">No Active Subscription</span>
                    </div>
                    <p className="text-sm text-yellow-700">
                      Subscribe to appear in the tutor marketplace and receive student inquiries.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-6">
              {Object.entries(subscriptionPlans).map(([planId, plan]) => (
                <Card key={planId} className={`shadow-xl ${planId === 'tutor_annual' ? 'border-2 border-green-400' : ''}`}>
                  {planId === 'tutor_annual' && (
                    <div className="bg-green-500 text-white text-center py-1 text-sm font-semibold">
                      Best Value - Save 2 Months!
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle>{plan.name}</CardTitle>
                    <CardDescription>
                      <span className="text-3xl font-bold text-gray-900">£{plan.price}</span>
                      <span className="text-gray-500">/{planId.includes('annual') ? 'year' : 'month'}</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 mb-6">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button 
                      onClick={() => handleSubscribe(planId)}
                      className={`w-full ${planId === 'tutor_annual' ? 'bg-green-600 hover:bg-green-700' : 'bg-purple-600 hover:bg-purple-700'}`}
                      disabled={profile?.subscription?.planId === planId && profile?.subscription?.status === 'active'}
                    >
                      {profile?.subscription?.planId === planId && profile?.subscription?.status === 'active' 
                        ? 'Current Plan' 
                        : 'Subscribe Now'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Inquiries Tab */}
          <TabsContent value="inquiries">
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" /> Student Inquiries
                </CardTitle>
                <CardDescription>
                  Messages from students and parents interested in your tutoring services
                </CardDescription>
              </CardHeader>
              <CardContent>
                {inquiries.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-600 mb-2">No Inquiries Yet</h3>
                    <p className="text-gray-500">
                      Once your profile is approved and you have an active subscription, 
                      students will be able to contact you here.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {inquiries.map((inquiry) => (
                      <Card key={inquiry.id} className="border">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-semibold">{inquiry.studentName}</p>
                              <p className="text-sm text-gray-500">Parent: {inquiry.parentName}</p>
                            </div>
                            <Badge variant="outline">{inquiry.subject} - {inquiry.level}</Badge>
                          </div>
                          <p className="text-gray-700 mb-3">{inquiry.message}</p>
                          <div className="flex justify-between items-center text-sm text-gray-500">
                            <span>{inquiry.email} | {inquiry.phone}</span>
                            <span>{new Date(inquiry.createdAt).toLocaleDateString()}</span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default TutorDashboard;
